import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ComponentDimensions } from '../../../../../types/Layout';
import { colors } from '../../../../../colors';
import { getBotColor } from '../../Charts/helpers';
import { Subscription } from '../../../../../types/subscription';
import {
  calculateIsColorTooDark,
  lightenColor,
} from '../../../../../helpers/colors';
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
} from 'victory';
import useAnimatedChartData from '../../../../../hooks/useAnimatedChartData';
import { SortingToggleOptions } from '../../../types';
import { useSelector } from 'react-redux';
import { ChartType } from '../../../../Detail/Charts/ProfitChart';
import { DataPoint } from '../../../../Detail/Charts/types';
import LoadingIcons from 'react-loading-icons';
import { stretchYValueOnSharedDomain } from '../../../../../helpers/Chart';
import { selectChartData } from '../../../../../store/slices/chartData';
import {
  buildReferenceLineData,
  EXODUS_REFERENCE_LINE,
  MRC_REFERENCE_LINE,
  SUB_1D_REFERENCE_LINE,
  ReferenceLinePatterns,
  TPT_REFERENCE_LINE,
} from '../../../../../helpers/Chart/referenceLines';

const BotItemChart = ({
  subscription,
  activeToggleOption,
}: {
  subscription: Subscription;
  activeToggleOption: SortingToggleOptions;
}) => {
  // Chart data is loaded once for the whole grid via the batched details query
  // in the Overview screen; this card only reads it from the store.
  const chartData = useSelector(selectChartData(subscription.id));

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    const parentRect = rootRef.current.getBoundingClientRect();
    setRootDimensions({
      width: parentRect.width,
      height: parentRect.height,
    });
  }, [rootDimensions]);

  // Stretching datasets over the same domain
  const selectedChartTypes = useMemo(() => {
    switch (activeToggleOption) {
      case SortingToggleOptions.TotalValue:
      case SortingToggleOptions.Diversity:
        return [
          ChartType.TotalStableBalance,
          ChartType.NoAlgoritmicProgression,
        ];

      case SortingToggleOptions.TotalStableProfit:
        return [
          ChartType.TotalStableBalance,
          ChartType.NoAlgoritmicProgression,
        ];

      case SortingToggleOptions.TotalAssetProfit:
        return [ChartType.TotalAssetBalance];

      case SortingToggleOptions.CurrentStableProfit:
        return [ChartType.StableBalance, ChartType.TotalStableBalance];

      case SortingToggleOptions.CurrentAssetProfit:
        return [ChartType.AssetBalance, ChartType.TotalAssetBalance];

      case SortingToggleOptions.NoAlgorithmicDifference:
        return [
          ChartType.TotalStableBalance,
          ChartType.NoAlgoritmicProgression,
        ];
    }
  }, [activeToggleOption]);

  const selectedStableValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter(
      (ct) =>
        ct === ChartType.NoAlgoritmicProgression ||
        ct === ChartType.TotalStableBalance ||
        ct === ChartType.StableBalance,
    );
  }, [selectedChartTypes]);

  const selectedAssetValueChartTypes: ChartType[] = useMemo(() => {
    return selectedChartTypes.filter(
      (ct) =>
        ct === ChartType.TotalAssetBalance || ct === ChartType.AssetBalance,
    );
  }, [selectedChartTypes]);

  const filterCurrentProfitTimestamp = useCallback(
    (d: { x: number; y: number }) => {
      const isCurrentProfit =
        activeToggleOption === SortingToggleOptions.CurrentStableProfit ||
        activeToggleOption === SortingToggleOptions.CurrentAssetProfit;

      if (!isCurrentProfit) {
        return true;
      }

      const latestTransactionTimestamp =
        subscription.latestTransaction?.transactionTimestamp ?? 0;

      // * 0.9999 to show a little stump before the latest transaction
      return d.x >= latestTransactionTimestamp * 0.9999;
    },
    [activeToggleOption, subscription.latestTransaction?.transactionTimestamp],
  );

  const rawTotalStableDataPoints = useMemo(() => {
    return (chartData?.dataPoints.total.stable ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(filterCurrentProfitTimestamp);
  }, [chartData?.dataPoints.total.stable, filterCurrentProfitTimestamp]);
  const rawNapDataPoints = useMemo(() => {
    return (chartData?.dataPoints.noAlgorithmicProgression ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(filterCurrentProfitTimestamp);
  }, [
    chartData?.dataPoints.noAlgorithmicProgression,
    filterCurrentProfitTimestamp,
  ]);
  const rawStableDataPoints = useMemo(() => {
    return (chartData?.dataPoints.stable?.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(filterCurrentProfitTimestamp);
  }, [chartData?.dataPoints.stable?.all, filterCurrentProfitTimestamp]);
  const rawTotalAssetDataPoints = useMemo(() => {
    return (chartData?.dataPoints.total.asset ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(filterCurrentProfitTimestamp);
  }, [chartData?.dataPoints.total.asset, filterCurrentProfitTimestamp]);
  const rawAssetDataPoints = useMemo(() => {
    return (chartData?.dataPoints.asset?.all ?? [])
      .map((dataPoint: DataPoint) => ({
        x: dataPoint.timestamp,
        y: dataPoint.value,
      }))
      .filter(filterCurrentProfitTimestamp);
  }, [chartData?.dataPoints.asset?.all, filterCurrentProfitTimestamp]);

  const rawTotalStableDataPointsMax = useMemo(
    () => Math.max(...rawTotalStableDataPoints.map((datum) => datum.y)),
    [rawTotalStableDataPoints],
  );
  const rawNapDataPointsMax = useMemo(
    () => Math.max(...rawNapDataPoints.map((datum) => datum.y)),
    [rawNapDataPoints],
  );
  const rawStableDataPointsMax = useMemo(
    () => Math.max(...rawStableDataPoints.map((datum) => datum.y)),
    [rawStableDataPoints],
  );
  const rawTotalAssetDataPointsMax = useMemo(
    () => Math.max(...rawTotalAssetDataPoints.map((datum) => datum.y)),
    [rawTotalAssetDataPoints],
  );

  const rawAssetDataPointsMax = useMemo(
    () => Math.max(...rawAssetDataPoints.map((datum) => datum.y)),
    [rawAssetDataPoints],
  );

  const stableValueMax = useMemo(() => {
    return Math.max(
      ...selectedStableValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            return rawNapDataPointsMax;

          case ChartType.TotalStableBalance:
            return rawTotalStableDataPointsMax;

          case ChartType.StableBalance:
            return rawStableDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [
    rawNapDataPointsMax,
    rawStableDataPointsMax,
    rawTotalStableDataPointsMax,
    selectedStableValueChartTypes,
  ]);
  const assetValueMax = useMemo(() => {
    return Math.max(
      ...selectedAssetValueChartTypes.map((ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            return rawTotalAssetDataPointsMax;

          case ChartType.AssetBalance:
            return rawAssetDataPointsMax;

          default:
            return 0;
        }
      }),
    );
  }, [
    rawAssetDataPointsMax,
    rawTotalAssetDataPointsMax,
    selectedAssetValueChartTypes,
  ]);

  const totalStableDataOriginalDomain = useMemo(() => {
    return rawTotalStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawTotalStableDataPoints, stableValueMax]);
  const napDataOriginalDomain = useMemo(() => {
    return rawNapDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawNapDataPoints, stableValueMax]);
  const stableDataOriginalDomain = useMemo(() => {
    return rawStableDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / stableValueMax,
    }));
  }, [rawStableDataPoints, stableValueMax]);
  const totalAssetDataOriginalDomain = useMemo(() => {
    return rawTotalAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / assetValueMax,
    }));
  }, [assetValueMax, rawTotalAssetDataPoints]);

  const assetDataOriginalDomain = useMemo(() => {
    return rawAssetDataPoints.map((datum) => ({
      ...datum,
      y: datum.y / assetValueMax,
    }));
  }, [assetValueMax, rawAssetDataPoints]);

  const stableValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.TotalStableBalance:
            acc.push(...totalStableDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
    totalStableDataOriginalDomain,
  ]);
  const stableValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...selectedStableValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.NoAlgoritmicProgression:
            acc.push(...napDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.TotalStableBalance:
            acc.push(...totalStableDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.StableBalance:
            acc.push(...stableDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    napDataOriginalDomain,
    selectedStableValueChartTypes,
    stableDataOriginalDomain,
    totalStableDataOriginalDomain,
  ]);
  const assetValueOriginalDomainMin = useMemo(() => {
    return Math.min(
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            acc.push(...totalAssetDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    assetDataOriginalDomain,
    selectedAssetValueChartTypes,
    totalAssetDataOriginalDomain,
  ]);
  const assetValueOriginalDomainMax = useMemo(() => {
    return Math.max(
      ...selectedAssetValueChartTypes.reduce<number[]>((acc, ct) => {
        switch (ct) {
          case ChartType.TotalAssetBalance:
            acc.push(...totalAssetDataOriginalDomain.map((d) => d.y));
            return acc;

          case ChartType.AssetBalance:
            acc.push(...assetDataOriginalDomain.map((d) => d.y));
            return acc;

          default:
            return acc;
        }
      }, []),
    );
  }, [
    assetDataOriginalDomain,
    selectedAssetValueChartTypes,
    totalAssetDataOriginalDomain,
  ]);

  const napData = useMemo(() => {
    return napDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    napDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const totalStableData = useMemo(() => {
    return totalStableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    totalStableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const stableData = useMemo(() => {
    return stableDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        stableValueOriginalDomainMin,
        stableValueOriginalDomainMax,
      ),
    }));
  }, [
    stableDataOriginalDomain,
    stableValueOriginalDomainMin,
    stableValueOriginalDomainMax,
  ]);
  const totalAssetData = useMemo(() => {
    return totalAssetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        assetValueOriginalDomainMin,
        assetValueOriginalDomainMax,
      ),
    }));
  }, [
    assetValueOriginalDomainMax,
    assetValueOriginalDomainMin,
    totalAssetDataOriginalDomain,
  ]);
  const assetData = useMemo(() => {
    return assetDataOriginalDomain.map((datum) => ({
      ...datum,
      y: stretchYValueOnSharedDomain(
        datum.y,
        assetValueOriginalDomainMin,
        assetValueOriginalDomainMax,
      ),
    }));
  }, [
    assetDataOriginalDomain,
    assetValueOriginalDomainMax,
    assetValueOriginalDomainMin,
  ]);

  const dataSets = useMemo(() => {
    if (!chartData) {
      return [[]];
    }

    switch (activeToggleOption) {
      case SortingToggleOptions.TotalValue:
      case SortingToggleOptions.Diversity:
        return [totalStableData, napData];

      case SortingToggleOptions.TotalStableProfit:
        return [totalStableData, napData];

      case SortingToggleOptions.TotalAssetProfit:
        return [totalAssetData];

      case SortingToggleOptions.CurrentStableProfit:
        return [totalStableData, stableData];

      case SortingToggleOptions.CurrentAssetProfit:
        return [totalAssetData, assetData];

      case SortingToggleOptions.NoAlgorithmicDifference:
        return [totalStableData, napData];
    }
  }, [
    activeToggleOption,
    assetData,
    chartData,
    napData,
    stableData,
    totalAssetData,
    totalStableData,
  ]);

  const botColor = useMemo(() => {
    const color = getBotColor(subscription.baseAsset);

    return calculateIsColorTooDark(color) ? lightenColor(color) : color;
  }, [subscription.baseAsset]);

  const isPositiveChart = useMemo(() => {
    if (!dataSets?.length || !dataSets?.[0]?.length) {
      return true;
    }

    const dataSet = dataSets[0];
    return dataSet[dataSet.length - 1].y >= dataSet[0].y;
  }, [dataSets]);

  const getChartColor = useCallback(
    (chartType: ChartType) => {
      switch (chartType) {
        case ChartType.TotalStableBalance:
        case ChartType.StableBalance:
          return {
            fill: `url(#${
              isPositiveChart ? 'positiveStableColor' : 'negativeStableColor'
            })`,
            stroke: isPositiveChart ? colors.mossGreen : colors.red,
          };

        case ChartType.TotalAssetBalance:
        case ChartType.AssetBalance:
          return {
            fill: `url(#botColor_${subscription.baseAsset})`,
            stroke: botColor,
          };

        case ChartType.NoAlgoritmicProgression:
          return {
            fill: 'url(#napColor)',
            stroke: colors.white,
          };
      }
    },
    [botColor, isPositiveChart, subscription.baseAsset],
  );

  const { animatedDataPoints, domain: animatedDomain } = useAnimatedChartData(
    subscription.id.toString(),
    dataSets,
    selectedChartTypes,
    false,
    2,
  );

  const chartPadding = useMemo(
    () => ({
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    }),
    [],
  );

  const hasData = useMemo(() => dataSets.some((d) => !!d.length), [dataSets]);

  const domain = useMemo(() => {
    if (
      activeToggleOption === SortingToggleOptions.CurrentStableProfit ||
      activeToggleOption === SortingToggleOptions.CurrentAssetProfit
    ) {
      // Only account for the x values of current stable/asset balance
      // + a little extra on the bottom

      const dataSet =
        activeToggleOption === SortingToggleOptions.CurrentStableProfit
          ? totalStableData
          : totalAssetData;
      const yValues = dataSet.map((d) => d.y);
      const domainY = [Math.min(...yValues) * 0.95, Math.max(...yValues)];

      return { ...animatedDomain, y: domainY as [number, number] };
    }

    return animatedDomain;
  }, [activeToggleOption, animatedDomain, totalAssetData, totalStableData]);

  const referenceLineTimestamp = TPT_REFERENCE_LINE.timestamp;
  const referenceLineLabel = TPT_REFERENCE_LINE.label;

  const startTPTReferenceLine = useMemo(() => {
    return buildReferenceLineData(referenceLineTimestamp, 0, 1);
  }, [referenceLineTimestamp]);

  const startTPTReferenceLabel = useMemo(() => {
    return { x: referenceLineTimestamp, y: 1, label: referenceLineLabel };
  }, [referenceLineTimestamp, referenceLineLabel]);

  const mrcReferenceLineTimestamp = MRC_REFERENCE_LINE.timestamp;
  const mrcReferenceLineLabel = MRC_REFERENCE_LINE.label;

  const startMRCReferenceLine = useMemo(() => {
    return buildReferenceLineData(mrcReferenceLineTimestamp, 0, 1);
  }, [mrcReferenceLineTimestamp]);

  const startMRCReferenceLabel = useMemo(() => {
    return { x: mrcReferenceLineTimestamp, y: 1, label: mrcReferenceLineLabel };
  }, [mrcReferenceLineTimestamp, mrcReferenceLineLabel]);

  const sub1dReferenceLineTimestamp = SUB_1D_REFERENCE_LINE.timestamp;
  const sub1dReferenceLineLabel = SUB_1D_REFERENCE_LINE.label;

  const startSub1dReferenceLine = useMemo(() => {
    return buildReferenceLineData(sub1dReferenceLineTimestamp, 0, 1);
  }, [sub1dReferenceLineTimestamp]);

  const startSub1dReferenceLabel = useMemo(() => {
    return {
      x: sub1dReferenceLineTimestamp,
      y: 1,
      label: sub1dReferenceLineLabel,
    };
  }, [sub1dReferenceLineTimestamp, sub1dReferenceLineLabel]);

  // EXODUS has no fixed date: it marks the start of this chart's own x domain.
  const exodusReferenceLineTimestamp = useMemo(() => {
    const start = domain?.x?.[0];
    return typeof start === 'number' ? start : undefined;
  }, [domain?.x]);

  const startExodusReferenceLine = useMemo(() => {
    if (exodusReferenceLineTimestamp === undefined) {
      return [];
    }
    return buildReferenceLineData(exodusReferenceLineTimestamp, 0, 1);
  }, [exodusReferenceLineTimestamp]);

  const startExodusReferenceLabel = useMemo(() => {
    return {
      x: exodusReferenceLineTimestamp ?? 0,
      y: 1,
      label: EXODUS_REFERENCE_LINE.label,
    };
  }, [exodusReferenceLineTimestamp]);

  return (
    <div
      ref={rootRef}
      style={{
        position: 'relative',
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        height: '94%',
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <svg style={{ position: 'absolute' }}>
        <defs>
          <linearGradient
            id="positiveStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.mossGreen} stopOpacity={0.3} />
            <stop
              offset="100%"
              stopColor={colors.mossGreen}
              stopOpacity={0.04}
            />
          </linearGradient>
          <linearGradient
            id="negativeStableColor"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id={`botColor_${subscription.baseAsset}`}
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={botColor} stopOpacity={0.6} />
            <stop offset="100%" stopColor={botColor} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient id="napColor" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.white} stopOpacity={0.4} />
            <stop offset="100%" stopColor={colors.white} stopOpacity={0.04} />
          </linearGradient>
          <linearGradient
            id="positiveMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.green} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.green} stopOpacity={0.08} />
          </linearGradient>
          <linearGradient
            id="negativeMonthGradient"
            x1="0%"
            y1="0%"
            x2="0%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colors.red} stopOpacity={0.3} />
            <stop offset="100%" stopColor={colors.red} stopOpacity={0.08} />
          </linearGradient>

          <ReferenceLinePatterns />
        </defs>
      </svg>

      {!!rootDimensions && !!hasData && (
        <>
          <VictoryChart
            height={rootDimensions.height}
            width={rootDimensions.width}
            scale="linear"
            padding={chartPadding}
            domain={domain}
          >
            {/* Hide x-axis */}
            <VictoryAxis
              style={{
                axis: { stroke: 'transparent' }, // Hide the axis line
                ticks: { stroke: 'transparent' }, // Hide the ticks
                tickLabels: { fill: 'transparent' }, // Hide the tick labels
              }}
            />
            {animatedDataPoints.map((selectedDataPoint, index) => (
              <VictoryArea
                key={`chart_${index}`}
                data={selectedDataPoint}
                style={{
                  data: {
                    ...getChartColor(selectedChartTypes[index]),
                    strokeWidth: 1,
                  },
                }}
              />
            ))}

            <VictoryLine
              data={startTPTReferenceLine}
              style={{
                data: {
                  stroke: `url(#${TPT_REFERENCE_LINE.patternId})`,
                  strokeWidth: 3,
                },
              }}
            />
            <VictoryScatter
              data={[startTPTReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: TPT_REFERENCE_LINE.color,
                    fontWeight: 'bold',
                    fontSize: 8,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-4}
                />
              }
            />
            <VictoryLine
              data={startMRCReferenceLine}
              style={{
                data: {
                  stroke: `url(#${MRC_REFERENCE_LINE.patternId})`,
                  strokeWidth: 3,
                },
              }}
            />
            <VictoryScatter
              data={[startMRCReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: MRC_REFERENCE_LINE.color,
                    fontWeight: 'bold',
                    fontSize: 8,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-4}
                />
              }
            />
            <VictoryLine
              data={startSub1dReferenceLine}
              style={{
                data: {
                  stroke: `url(#${SUB_1D_REFERENCE_LINE.patternId})`,
                  strokeWidth: 3,
                },
              }}
            />
            <VictoryScatter
              data={[startSub1dReferenceLabel]}
              size={0}
              labels={({ datum }) => datum.label}
              labelComponent={
                <VictoryLabel
                  style={{
                    fill: SUB_1D_REFERENCE_LINE.color,
                    fontWeight: 'bold',
                    fontSize: 8,
                    opacity: 0.7,
                  }}
                  textAnchor="end"
                  dy={8}
                  dx={-4}
                />
              }
            />
            {/* EXODUS sits on the domain's left edge, so its label reads to
             *  the right of the line instead of to the left. */}
            {!!startExodusReferenceLine.length && (
              <VictoryLine
                data={startExodusReferenceLine}
                style={{
                  data: {
                    stroke: `url(#${EXODUS_REFERENCE_LINE.patternId})`,
                    strokeWidth: 3,
                  },
                }}
              />
            )}
            {!!startExodusReferenceLine.length && (
              <VictoryScatter
                data={[startExodusReferenceLabel]}
                size={0}
                labels={({ datum }) => datum.label}
                labelComponent={
                  <VictoryLabel
                    style={{
                      fill: EXODUS_REFERENCE_LINE.color,
                      fontWeight: 'bold',
                      fontSize: 8,
                      opacity: 0.7,
                    }}
                    textAnchor="start"
                    dy={8}
                    dx={4}
                  />
                }
              />
            )}
          </VictoryChart>
        </>
      )}

      {!!rootDimensions && !hasData && (
        <LoadingIcons.Grid width={20} height={20} />
      )}
    </div>
  );
};

export default BotItemChart;
